import React, { useMemo, useState } from 'react';
import { Coins, ChevronRight, ChevronDown } from 'lucide-react';

// İşlem Geçmişi → "COIN-M" sekmesi (salt okunur, bot_id = binance1-cm).
//
// Kullanıcı kararı (30/09/2026): USD cinsinden PnL YOK. Her tutar KENDİ coin'i
// cinsinden; toplamlar COIN BAŞINA (ETH ile BTC toplanamaz). Miktar KONTRAT.
// Grup etiketleme ilk sürümde yok (grup PnL'i USD; COIN-M ayrı tasarım konusu).

interface Row { id: string; bot_id: string; symbol: string; side?: string; price?: number;
  quantity?: number; realizedPnl?: number; fee?: number; executedAtMillis?: number;
  fundingFee?: number; createdTime?: number; created_time?: number; }

interface GridEvent { ts: number; label: string; amount: number; }
interface Grid { id: string; asset: string; pair: string; openTs: number | null; closeTs: number | null;
  deposit: number; withdraw: number; pnl: number | null; unmatched?: boolean; events?: GridEvent[]; }

// Binance gelir kaydındaki `info` etiketi → Türkçe. Bilinmeyen etiket olduğu gibi gösterilir.
const EVENT_LABEL: Record<string, string> = {
  CM_GRID_CREATE: 'Açılış', CM_ADD_MARGIN: 'Margin ekleme',
  CM_REMOVE_MARGIN: 'Margin çıkarma', CM_GRID_CLOSE: 'Kapanış',
};

interface Props { t: any; trades: Row[]; funding: Row[]; grids?: Grid[]; fromMs?: number; toMs?: number; }

// ETHUSD_PERP → ETH · BTCUSD_251226 → BTC
const coinOf = (sym: string) => (sym && sym.includes('USD') ? sym.split('USD')[0] : sym || '?');
const tsOf = (r: Row) => Number(r.executedAtMillis ?? r.createdTime ?? r.created_time ?? 0);
const fmtC = (v: number) => (v > 0 ? '+' : '') + v.toLocaleString('tr-TR', { maximumFractionDigits: 8 });
const fmtN = (v: number, d = 8) => v.toLocaleString('tr-TR', { maximumFractionDigits: d });
const fmtD = (ms: number) => ms ? new Date(ms).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'medium' }) : '—';

export default function CoinMHistory({ t, trades, funding, grids = [], fromMs, toMs }: Props) {
  const [coinF, setCoinF] = useState('all');
  const [openGrid, setOpenGrid] = useState<Set<string>>(new Set());   // dökümü açık grid'ler
  const toggleGrid = (id: string) => setOpenGrid(prev => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const inRange = (ms: number) => (!fromMs || ms >= fromMs) && (!toMs || ms <= toMs);

  const tr = useMemo(() => trades
    .filter(r => inRange(tsOf(r)) && (coinF === 'all' || coinOf(r.symbol) === coinF))
    .sort((a, b) => tsOf(b) - tsOf(a)), [trades, coinF, fromMs, toMs]);   // eslint-disable-line
  const fd = useMemo(() => funding
    .filter(r => inRange(tsOf(r)) && (coinF === 'all' || coinOf(r.symbol) === coinF))
    .sort((a, b) => tsOf(b) - tsOf(a)), [funding, coinF, fromMs, toMs]);  // eslint-disable-line

  const coins = useMemo(() => Array.from(new Set([
    ...[...trades, ...funding].map(r => coinOf(r.symbol)), ...grids.map(g => g.asset)])).sort(),
    [trades, funding, grids]);
  // Kapanan grid'ler: tarih filtresi KAPANIŞ zamanına göre (sonuç o an kesinleşti)
  const gr = useMemo(() => grids.filter(g => inRange(g.closeTs || 0) && (coinF === 'all' || g.asset === coinF)),
    [grids, coinF, fromMs, toMs]);   // eslint-disable-line

  // Coin başına toplamlar. Net = gerçekleşmiş PnL − ücret + funding.
  // Coin başına toplamlar: elle COIN-M işlemleri + funding + KAPANMIŞ grid'ler.
  // Grid PnL (withdraw − deposit) grid'in kendi ücret ve funding'ini ZATEN içeriyor
  // (strateji hesabında ödeniyor, API'de ayrı görünmüyor) → Ücret/Funding sütunlarına
  // eklenmez, yalnız Net'e girer. Çalışan grid'in sonucu henüz yok → dahil değil.
  // Net = gerçekleşmiş PnL − ücret + funding + grid PnL = COIN-M cüzdanının gerçek değişimi.
  const sums = useMemo(() => {
    const m: Record<string, { pnl: number; fee: number; funding: number; n: number; contracts: number;
                              grid: number; gridN: number }> = {};
    const get = (c: string) => (m[c] ||= { pnl: 0, fee: 0, funding: 0, n: 0, contracts: 0, grid: 0, gridN: 0 });
    for (const r of tr) {
      const s = get(coinOf(r.symbol));
      s.pnl += r.realizedPnl || 0; s.fee += r.fee || 0; s.n += 1; s.contracts += r.quantity || 0;
    }
    for (const r of fd) get(coinOf(r.symbol)).funding += r.fundingFee || 0;
    for (const g of gr) {
      if (g.pnl == null) continue;          // açılışı bilinmeyen grid: sonuç uydurulmaz
      const s = get(g.asset); s.grid += g.pnl; s.gridN += 1;
    }
    return m;
  }, [tr, fd, gr]);

  const th = `px-3 py-2.5 text-left font-medium ${t.muted}`;
  const card = `${t.card} border ${t.cardBorder} rounded-xl`;

  return (
    <div className="space-y-5">
      {coins.length > 1 && (
        <div className="flex items-center gap-2 text-xs">
          <span className={t.muted}>Coin:</span>
          {['all', ...coins].map(c => (
            <button key={c} onClick={() => setCoinF(c)}
              className={`px-2.5 py-1 rounded-md border ${coinF === c ? 'border-sky-500/60 text-sky-300' : `${t.cardBorder} ${t.muted}`}`}>
              {c === 'all' ? 'Tümü' : c}
            </button>
          ))}
        </div>
      )}

      {Object.keys(sums).length === 0 && gr.length === 0 ? (
        <div className={`${card} p-8 text-center ${t.muted} text-sm`}>COIN-M işlem ya da funding kaydı yok.</div>
      ) : Object.entries(sums).map(([c, s]) => {
        const net = s.pnl - s.fee + s.funding + s.grid;
        return (
          <div key={c} className={`${card} p-4`}>
            <p className={`text-xs mb-3 ${t.muted} flex items-center gap-1.5`}><Coins size={12} /> {c} — coin cinsinden</p>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
              {[
                ['Gerçekleşmiş PnL', s.pnl, true],
                ['Ücret', -s.fee, true],
                ['Funding', s.funding, true],
                [`Grid PnL${s.gridN ? ` · ${s.gridN}` : ''}`, s.grid, true],
                ['Net', net, true],
                ['İşlem · kontrat', null, false],
              ].map(([label, v, signed]) => (
                <div key={label as string}>
                  <p className={`text-[11px] ${t.muted}`}
                    title={(label as string).startsWith('Grid') ? 'Kapanmış grid\'lerin withdraw − deposit toplamı; grid\'in kendi ücret ve funding\'i içinde' : undefined}>
                    {label as string}</p>
                  {v === null
                    ? <p className={`font-mono ${t.text}`}>{s.n} · {fmtN(s.contracts, 0)}</p>
                    : <p className={`font-mono font-semibold ${(v as number) >= 0 ? t.positive : t.negative} ${label === 'Net' ? 'text-base' : ''}`}>
                        {signed ? fmtC(v as number) : fmtN(v as number)} {c}
                      </p>}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Grid'ler — kapanan COIN-M grid botları. İşlemler kartına GİREMEZ: orada fiyat ve
          kontrat var, grid'in ise API'de dolumu yok; yalnız strateji hesabına giden (deposit)
          ve dönen (withdraw) sermaye biliniyor. Net PnL = withdraw − deposit, coin cinsinden. */}
      {gr.length > 0 && (
        <div className={`${card} overflow-x-auto`}>
          <p className={`px-3 pt-3 text-xs font-semibold ${t.text}`}>Grid'ler · {gr.length}</p>
          <table className="w-full text-xs">
            <thead><tr className={`${t.tableHeader} border-b`}>
              {['Tarih', 'Sembol', 'Deposit', 'Withdraw', 'Net PnL'].map(h => <th key={h} className={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {gr.map(g => {
                const isOpen = openGrid.has(g.id);
                const evs = g.events || [];
                return (
                  <React.Fragment key={g.id}>
                    <tr className={`border-b ${t.tableRow} ${evs.length ? 'cursor-pointer' : ''}`}
                      onClick={() => evs.length && toggleGrid(g.id)}
                      title={evs.length ? 'Hareketleri göster/gizle' : undefined}>
                      <td className={`px-3 py-2 ${t.muted}`}>
                        <span className="inline-flex items-center gap-1">
                          {evs.length > 0 && (isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />)}
                          {g.openTs ? fmtD(g.openTs) : '?'} → {fmtD(g.closeTs || 0)}
                        </span>
                      </td>
                      <td className={`px-3 py-2 font-semibold ${t.text}`}>{g.pair}</td>
                      <td className={`px-3 py-2 font-mono ${t.text}`}>
                        {g.unmatched ? <span className={t.muted} title="Açılış transferi 30 günlük geri dolum penceresinden önce">bilinmiyor</span>
                                     : `${fmtN(g.deposit)} ${g.asset}`}
                      </td>
                      <td className={`px-3 py-2 font-mono ${t.text}`}>{fmtN(g.withdraw)} {g.asset}</td>
                      <td className={`px-3 py-2 font-mono font-semibold ${
                        g.pnl == null ? t.muted : g.pnl > 0 ? t.positive : g.pnl < 0 ? t.negative : t.muted}`}>
                        {g.pnl == null ? '—' : `${fmtC(g.pnl)} ${g.asset}`}
                      </td>
                    </tr>
                    {/* Döküm: strateji hesabına giren (−) ve dönen (+) her hareket, zaman sırasıyla.
                        İşaret CÜZDAN açısından: − sermaye strateji hesabına gitti, + geri döndü. */}
                    {isOpen && evs.map((e, i) => (
                      <tr key={`${g.id}-${i}`} className={`border-b ${t.tableRow} opacity-80`}>
                        <td className={`pl-9 pr-3 py-1.5 text-[11px] ${t.muted}`}>{fmtD(e.ts)}</td>
                        <td className={`px-3 py-1.5 text-[11px] ${t.text}`} colSpan={3}>
                          {EVENT_LABEL[e.label] || e.label || 'Transfer'}
                        </td>
                        <td className={`px-3 py-1.5 text-[11px] font-mono ${e.amount >= 0 ? t.positive : t.muted}`}>
                          {fmtC(e.amount)} {g.asset}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tr.length > 0 && (
        <div className={`${card} overflow-x-auto`}>
          <p className={`px-3 pt-3 text-xs font-semibold ${t.text}`}>İşlemler · {tr.length}</p>
          <table className="w-full text-xs">
            <thead><tr className={`${t.tableHeader} border-b`}>
              {['Tarih', 'Sembol', 'Yön', 'Fiyat', 'Kontrat', 'PnL', 'Ücret'].map(h => <th key={h} className={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {tr.map(r => {
                const c = coinOf(r.symbol);
                return (
                  <tr key={r.id} className={`border-b ${t.tableRow}`}>
                    <td className={`px-3 py-2 ${t.muted}`}>{fmtD(tsOf(r))}</td>
                    <td className={`px-3 py-2 font-semibold ${t.text}`}>{r.symbol}</td>
                    <td className={`px-3 py-2 font-semibold ${r.side === 'LONG' ? t.positive : t.negative}`}>
                      {r.side === 'LONG' ? 'BUY' : 'SELL'}
                    </td>
                    <td className={`px-3 py-2 font-mono ${t.text}`}>{fmtN(r.price || 0, 2)}</td>
                    <td className={`px-3 py-2 font-mono ${t.text}`}>{fmtN(r.quantity || 0, 0)}</td>
                    <td className={`px-3 py-2 font-mono ${(r.realizedPnl || 0) > 0 ? t.positive : (r.realizedPnl || 0) < 0 ? t.negative : t.muted}`}>
                      {r.realizedPnl ? `${fmtC(r.realizedPnl)} ${c}` : '—'}
                    </td>
                    <td className={`px-3 py-2 font-mono ${t.muted}`}>{fmtN(r.fee || 0)} {c}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {fd.length > 0 && (
        <div className={`${card} overflow-x-auto`}>
          <p className={`px-3 pt-3 text-xs font-semibold ${t.text}`}>Funding · {fd.length}</p>
          <table className="w-full text-xs">
            <thead><tr className={`${t.tableHeader} border-b`}>
              {['Tarih', 'Sembol', 'Tutar'].map(h => <th key={h} className={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {fd.map(r => (
                <tr key={r.id} className={`border-b ${t.tableRow}`}>
                  <td className={`px-3 py-2 ${t.muted}`}>{fmtD(tsOf(r))}</td>
                  <td className={`px-3 py-2 font-semibold ${t.text}`}>{r.symbol}</td>
                  <td className={`px-3 py-2 font-mono ${(r.fundingFee || 0) >= 0 ? t.positive : t.negative}`}>
                    {fmtC(r.fundingFee || 0)} {coinOf(r.symbol)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
